import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@shared/lib/utils';

export function Skeleton({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('animate-pulse rounded-lg bg-muted', className)} {...props} />;
}

interface SkeletonStatusProps {
  children: ReactNode;
  className?: string;
  label?: string;
}

/** Accessible wrapper for loading placeholders (role=status, aria-busy). */
export function SkeletonStatus({ children, className, label }: SkeletonStatusProps) {
  return (
    <div className={className} role="status" aria-busy="true" aria-live="polite">
      {label ? <span className="sr-only">{label}</span> : null}
      {children}
    </div>
  );
}

/** Figma variant: Default — avatar + two text lines. */
export function SkeletonAvatarRow({ className }: { className?: string }) {
  return (
    <div className={cn('flex items-center gap-4', className)}>
      <Skeleton className="size-10 shrink-0 rounded-full" />
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-[9.375rem] max-w-full" />
        <Skeleton className="h-4 w-[6.25rem] max-w-full" />
      </div>
    </div>
  );
}

/** Figma variant: Card — title lines + large content block. */
export function SkeletonCardBlock({ className }: { className?: string }) {
  return (
    <div className={cn('flex w-full flex-col gap-6', className)}>
      <div className="flex w-full flex-col gap-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
      </div>
      <Skeleton className="min-h-[12rem] w-full rounded-lg sm:min-h-[16rem]" />
    </div>
  );
}

/** Figma variant: Text — two lines of different width. */
export function SkeletonTextBlock({
  className,
  primaryClassName,
  secondaryClassName,
}: {
  className?: string;
  primaryClassName?: string;
  secondaryClassName?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <Skeleton className={cn('h-4 w-full max-w-[13.875rem]', primaryClassName)} />
      <Skeleton className={cn('h-4 w-[10.4375rem] max-w-full', secondaryClassName)} />
    </div>
  );
}

export function SkeletonTableRows({
  rows = 5,
  className,
  rowClassName,
}: {
  rows?: number;
  className?: string;
  rowClassName?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {Array.from({ length: rows }).map((_, index) => (
        <Skeleton key={index} className={cn('h-10 w-full', rowClassName)} />
      ))}
    </div>
  );
}
